import React, { useEffect, useState } from "react";

import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { Button, Divider, IconButton, Stack } from "@mui/material";
import { TableSettings } from "components/DealGroupedTable/TableSettings";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DEAL_LIST_BASE_CHARACTERISTICS, DealViews, PIPELINE_SYSTEM_GROUP_DIMENSIONS } from "constants/deal";
import {
  useDealActions,
  useDealListViewConfigSelector,
  useDealQueriesSelect,
  usePipelineViewConfigSelector
} from "store";
import { PipelineDimensionOption } from "store/types";

import { useGroupingView } from "../GroupingViewContext";
import { GroupBySelector } from "./GroupBySelector";
import { PipelinePropertiesPopup } from "./PipelinePropertiesPopup";

const pillSx = {
  textTransform: "none",
  borderRadius: "20px",
  height: "24px",
  minHeight: "24px",
  fontSize: "13px",
  color: "#0a3142",
  borderColor: "#dae0e2",
  px: "12px",
  whiteSpace: "nowrap"
} as const;

export const PipelineControls: React.FC = () => {
  const {
    updateQueries,
    getCharacteristicsAction,
    updatePipelineViewConfigAction,
    getDealsTableColumnsActions,
    updateDealListViewConfigAction
  } = useDealActions();
  const {
    view,
    dealListGroupBy,
    dealListCharacteristics,
    dealListCharacteristicsOptions,
    pipelineGroupBy,
    pipelineCardCharacteristics,
    pipelineCardCharacteristicOptions,
    pipelineHiddenStageIds
  } = useDealQueriesSelect();
  const savedConfig = usePipelineViewConfigSelector();
  const savedDealListConfig = useDealListViewConfigSelector();
  const { collapseAll, expandAll, stages } = useGroupingView();

  const isTable = view === DealViews.TABLE;

  // Pipeline: characteristic names for the "Add Characteristic" picker. Table: supported column options.
  const [availableCharacteristics, setAvailableCharacteristics] = useState<string[]>([]);
  // Group By dimensions: every account characteristic (name sent to the API + shown as label). Both views.
  const [groupByDimensions, setGroupByDimensions] = useState<PipelineDimensionOption[]>([]);
  // Table: all column titles available in the "Add Characteristic" picker (from the deals-list columns endpoint).
  const [columnTitles, setColumnTitles] = useState<string[]>([]);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);

  // Hydrate the configs saved in user settings (server-side) into the live deal queries, once on mount.
  useEffect(() => {
    if (savedConfig && Object.keys(savedConfig).length) updateQueries(savedConfig);
    if (savedDealListConfig && Object.keys(savedDealListConfig).length) updateQueries(savedDealListConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load characteristics once for BOTH views: card "Add Characteristic" picker (pipeline) and the
  // Group By dimensions (both). Names are collapsed since the account can have several with the same name.
  useEffect(() => {
    let active = true;
    getCharacteristicsAction()
      .then((characteristics) => {
        if (!active) return;
        const names = [...new Set(characteristics.map((characteristic) => characteristic.name).filter(Boolean))];
        setAvailableCharacteristics(names);
        // System dimensions (Asset Class, Sub Asset Class) first, then characteristics — deduped by name.
        const systemKeys = new Set(PIPELINE_SYSTEM_GROUP_DIMENSIONS.map((dimension) => dimension.key));
        const characteristicDimensions = names
          .filter((name) => !systemKeys.has(name))
          .map((name) => ({ key: name, label: name }));
        setGroupByDimensions([...PIPELINE_SYSTEM_GROUP_DIMENSIONS, ...characteristicDimensions]);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isTable) return;
    let active = true;
    getDealsTableColumnsActions()
      .then((defs) => {
        if (!active) return;
        setColumnTitles(defs.map((column) => column.title));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [isTable, getDealsTableColumnsActions]);

  // Persist the WHOLE pipeline config blob (server stores it as one JSON value under the key).
  const persist = (
    patch: Partial<{
      pipelineGroupBy: string[];
      pipelineCardCharacteristics: string[];
      pipelineCardCharacteristicOptions: string[];
      pipelineHiddenStageIds: number[];
    }>
  ) => {
    updatePipelineViewConfigAction({
      pipelineGroupBy: patch.pipelineGroupBy ?? pipelineGroupBy,
      pipelineCardCharacteristics: patch.pipelineCardCharacteristics ?? pipelineCardCharacteristics,
      pipelineCardCharacteristicOptions: patch.pipelineCardCharacteristicOptions ?? pipelineCardCharacteristicOptions,
      pipelineHiddenStageIds: patch.pipelineHiddenStageIds ?? pipelineHiddenStageIds
    }).catch(() => undefined); // best-effort; live state already updated via updateQueries
  };

  // Persist the WHOLE deal-list (table) config blob (server stores it as one JSON value under the key).
  const persistDealList = (
    patch: Partial<{
      dealListGroupBy: string[];
      dealListCharacteristics: string[];
      dealListCharacteristicsOptions: string[];
    }>
  ) => {
    updateDealListViewConfigAction({
      dealListGroupBy: patch.dealListGroupBy ?? dealListGroupBy,
      dealListCharacteristics: patch.dealListCharacteristics ?? dealListCharacteristics,
      dealListCharacteristicsOptions: patch.dealListCharacteristicsOptions ?? dealListCharacteristicsOptions
    }).catch(() => undefined); // best-effort; live state already updated via updateQueries
  };

  const handleGroupByChange = (groupBy: string[]) => {
    if (isTable) {
      updateQueries({ dealListGroupBy: groupBy });
      persistDealList({ dealListGroupBy: groupBy });
      return;
    }
    updateQueries({ pipelineGroupBy: groupBy });
    persist({ pipelineGroupBy: groupBy });
  };

  const handleApplyColumns = (characteristics: string[], optionsList: string[]) => {
    updateQueries({ dealListCharacteristics: characteristics, dealListCharacteristicsOptions: optionsList });
    persistDealList({ dealListCharacteristics: characteristics, dealListCharacteristicsOptions: optionsList });
  };

  const handleApplyProperties = (
    cardCharacteristics: string[],
    cardOptionsList: string[],
    hiddenStageIds: number[]
  ) => {
    updateQueries({
      pipelineCardCharacteristics: cardCharacteristics,
      pipelineCardCharacteristicOptions: cardOptionsList,
      pipelineHiddenStageIds: hiddenStageIds
    });
    persist({
      pipelineCardCharacteristics: cardCharacteristics,
      pipelineCardCharacteristicOptions: cardOptionsList,
      pipelineHiddenStageIds: hiddenStageIds
    });
  };

  const closeSettings = () => setSettingsAnchor(null);

  const groupBy = isTable ? dealListGroupBy : pipelineGroupBy;
  const grouped = groupBy.length > 0;

  return (
    <Stack direction="row" alignItems="center" gap="10px">
      {grouped && (
        <>
          <Button variant="outlined" onClick={collapseAll} sx={pillSx}>
            Collapse All
          </Button>
          <Button variant="outlined" onClick={expandAll} sx={pillSx}>
            Expand All
          </Button>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "#dae0e2" }} />
        </>
      )}

      <GroupBySelector
        dimensions={groupByDimensions}
        groupBy={groupBy}
        onChange={handleGroupByChange}
        maxDepth={isTable ? 2 : 1}
      />

      <Divider orientation="vertical" flexItem sx={{ borderColor: "#dae0e2" }} />
      <MaterialTooltip title={isTable ? "Columns" : "Card & stage settings"} isShow>
        <IconButton sx={{ width: "24px", height: "24px" }} onClick={(e) => setSettingsAnchor(e.currentTarget)}>
          <TuneRoundedIcon sx={{ fontSize: "18px", color: "#0a3142" }} />
        </IconButton>
      </MaterialTooltip>

      {isTable ? (
        <TableSettings
          anchorEl={settingsAnchor}
          open={Boolean(settingsAnchor)}
          onClose={closeSettings}
          optionsList={dealListCharacteristicsOptions}
          availableColumns={columnTitles}
          selected={dealListCharacteristics}
          baseColumns={DEAL_LIST_BASE_CHARACTERISTICS}
          onApply={handleApplyColumns}
        />
      ) : (
        <PipelinePropertiesPopup
          anchorEl={settingsAnchor}
          open={Boolean(settingsAnchor)}
          onClose={closeSettings}
          cardOptionsList={pipelineCardCharacteristicOptions}
          availableCharacteristics={availableCharacteristics}
          cardCharacteristics={pipelineCardCharacteristics}
          hiddenStageIds={pipelineHiddenStageIds}
          stages={stages}
          onApply={handleApplyProperties}
        />
      )}
    </Stack>
  );
};
